import React from 'react';
import { DragSource } from 'react-dnd';
import { Label, Clearfix, Tooltip, OverlayTrigger as DefaultOverlayTrigger } from 'react-bootstrap';
import moment from 'moment';
import 'moment-timezone';

import { getThumbFile } from 'helpers/items';
import { getColor } from 'helpers/charts';

import OverlayTrigger from 'components/OverlayTrigger';
import RichEditorContent from 'components/RichEditorContent';

import IntlDate from 'components/layout/IntlDate';
import IntlDateTime from 'components/layout/IntlDateTime';
import IntlDateRelative from 'components/layout/IntlDateRelative';

const cardSource = {
  beginDrag(props) {
    return {
      id: props.lead.id,
      originalIndex: props.findCard(props.lead.id).index,
    };
  },
};

@DragSource('card', cardSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
}))
class LeadCard extends React.Component {
  onClick = e => {
    const { lead, onClick } = this.props;
    onClick(e, lead);
  };

  doNothing = e => {
    e.preventDefault();
  };

  render() {
    const { lead, isDragging, connectDragSource, messages, selected } = this.props;

    const leadCreationDate = moment(lead.cdate);
    const momentWeekAgo = moment()
      .subtract(7, 'days')
      .startOf('day');
    const momentFourHoursAgo = moment().subtract(4, 'hours');
    const momentOneDayAgo = moment().subtract(1, 'days');
    const eventDate = moment(lead.mdate).isAfter(momentWeekAgo) ? (
      <IntlDateRelative date={lead.mdate} />
    ) : (
      <IntlDate date={lead.mdate} />
    );

    const assigneeName = lead.assignee
      ? lead.assignee.profile.first_name + ' ' + lead.assignee.profile.last_name
      : messages.unassigned;

    let avatarThumbLink = null;
    if (lead.assignee && lead.assignee.profile && lead.assignee.profile.avatar) {
      const imageThumb = getThumbFile(lead.assignee.profile.avatar);
      avatarThumbLink = imageThumb ? imageThumb.web_link : null;
    }

    const assigneeAvatar = avatarThumbLink ? (
      <img height={30} src={avatarThumbLink} title={assigneeName} alt={assigneeName} />
    ) : (
      <i
        className={`fal fa-fw fa-user-circle text-${lead.assignee ? 'master-light' : 'danger'}`}
        style={{ fontSize: '26px' }}
        title={assigneeName}
      />
    );

    const commentsCount = lead.comments ? lead.comments.length : 0;
    const watchingCount = lead.watching_count ? lead.watching_count : 0;

    let priorityClass = 'normal ';
    switch (lead.priority) {
      case 0:
        priorityClass = 'low ';
        break;

      case 2:
        priorityClass = 'high ';
        break;

      case 3:
        priorityClass = 'critical ';
        break;
    }

    let label;
    if (lead.is_won) {
      label = (
        <Label bsStyle="success" className="m-r-10" style={{ fontSize: 'inherit' }}>
          {messages.won}
        </Label>
      );
    } else if (lead.is_lost) {
      label = (
        <Label bsStyle="danger" className="m-r-10" style={{ fontSize: 'inherit' }}>
          {messages.lost}
        </Label>
      );
    } else if (lead.is_deleted) {
      label = (
        <Label bsStyle="danger" className="label-important m-r-10" style={{ fontSize: 'inherit' }}>
          {messages.deleted}
        </Label>
      );
    } else {
      // label = <Label bsStyle="info" className="m-r-10" style={{ fontSize: 'inherit' }}>{messages.open}</Label>
    }

    const isHot = leadCreationDate.isAfter(momentFourHoursAgo);
    const isWarm = leadCreationDate.isAfter(momentOneDayAgo);

    return connectDragSource(
      <div
        className={
          'card lead-card social-card share card-default full-width ' +
          (isDragging ? 'card-dragging ' : '') +
          (selected ? 'bg-primary-lighter ' : '') +
          'lead-priority-' +
          priorityClass
        }
        onClick={this.onClick}
      >
        <div className="card-header ui-sortable-handle">
          <div className="card-title" style={{ maxWidth: '100%' }}>
            {label} {isHot && <i className="text-danger fas fa-fire" />}
            {!isHot && isWarm && <i className="text-warning fas fa-stopwatch" />}{' '}
            <i className="fad fa-circle" style={{ color: '#' + getColor(lead.color || 1, 81) }} /> {lead.title}
          </div>
          <DefaultOverlayTrigger placement="top" overlay={<Tooltip>{assigneeName}</Tooltip>}>
            <div className="card-assignee">{assigneeAvatar}</div>
          </DefaultOverlayTrigger>
        </div>
        <div className="card-content p-l-20 p-r-20 p-t-5 p-b-5">
          <RichEditorContent content={'<div>' + (lead.description ? lead.description : '') + '</div>'} />
        </div>
        <div className="card-footer">
          <Clearfix>
            <OverlayTrigger
              placement="bottom"
              wrappingComponent={Tooltip}
              overlay={
                <div className="text-left">
                  {messages.date_created}: <IntlDateTime date={lead.cdate} />
                  <br />
                  {messages.date_updated}: <IntlDateTime date={lead.mdate} />
                  <br />
                  {messages.due_date}: <IntlDateTime date={lead.due_date} />
                </div>
              }
            >
              <div className="time">{eventDate}</div>
            </OverlayTrigger>
            <ul className="reactions">
              <li className="m-r-10">
                <a href="#countComments" onClick={this.doNothing}>
                  {commentsCount} <i className="far fa-comment" />
                </a>
              </li>
              <li>
                <a href="#countWatching" onClick={this.doNothing}>
                  {watchingCount} <i className="fa fa-eye" />
                </a>
              </li>
            </ul>
          </Clearfix>

          {!!lead.tags && !!lead.tags.length && (
            <div>
              {lead.tags.map(tag => {
                return (
                  <React.Fragment key={tag}>
                    <Label bsSize="xs" className="m-r-5 m-b-5 inline">
                      {tag}
                    </Label>{' '}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}
LeadCard.defaultProps = {
  onClick: () => {},
};

export default LeadCard;
